import { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useKibanaLogger } from '../../../shared/hooks/kibana-logger/kibana-logger';
import { DEFAULT_ERROR_MSG } from '../../../shared/ui/urql-and-tanstack-error-message/urql-and-tanstack-error-message';
import styles from './dashboard-error-boundary.module.css';

const DashboardErrorFallback = () => (
  <div data-testid="error-boundary" className={styles.fallback}>
    <p className={styles.title}>Something went wrong</p>
    <p className={styles.body}>{DEFAULT_ERROR_MSG}</p>
  </div>
);

export const DashboardErrorBoundary = ({
  children,
  context = 'DashboardErrorBoundary',
}: {
  children: React.ReactNode;
  context?: string;
}) => {
  const { kibanaLogger } = useKibanaLogger();

  const handleError = useCallback(
    (error: Error, info: { componentStack?: string | null }) => {
      console.error(context, error);

      if (kibanaLogger === undefined) return;

      const baseMessage = error.stack
        ? `${context}: ${error.message}\n${error.stack}`
        : `${context}: ${error.message}`;

      const fullMessage = info.componentStack
        ? `${baseMessage}\n\nComponent Stack:${info.componentStack}`
        : baseMessage;

      kibanaLogger.error(fullMessage, {
        context,
      });
    },
    [kibanaLogger, context]
  );

  return (
    <ErrorBoundary
      FallbackComponent={DashboardErrorFallback}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};
